import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Avatar,
  Tooltip,
  Checkbox,
  Snackbar
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import {
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useItemCart } from '../../contexts/ItemCartContext';
import { formatPriceToDec, formatPriceToUsd, formatPriceToCredits, convertUsdToDec, convertUsdToCredits } from '../../scenes/market/priceUtils';


/**
 * ItemDetailsDialog Component - Shows detailed listings for a specific item
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Object} props.item - The item data to show details for
 * @returns {JSX.Element} - The rendered dialog component
 */
const ItemDetailsDialog = ({ open, onClose, item }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { addToItemCart } = useItemCart();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedListings, setSelectedListings] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  // Define loadListings function with correct API structure and fallback logic
  const loadListings = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // The API wants assetName/detailId exactly as returned by the market landing endpoint
      // (e.g. "PACKS", "TOTEMS") - no singularization or other mapping needed. An earlier
      // version guessed at singular variants ("PACK", "TOTEM", ...), which the API rejects
      // with 400 "Invalid assetName" - that wasted a request on every dialog open before
      // falling back to the correct value.
      const payload = {
        assetName: item.assetName,
        detailIds: item.detailId ? [item.detailId] : [],
        sort: {
          field: "price",
          order: "asc"
        }
      };

      const response = await axios.post('https://vapi.splinterlands.com/market/listing-items', payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      // Check for success response with data.items structure
      if (response.data && response.data.status === 'success' && response.data.data && Array.isArray(response.data.data.items)) {
        const apiItems = response.data.data.items;

        const realListings = apiItems.map((listing, index) => ({
          id: listing.listingId || listing.listingItemId || `listing_${index}`,
          listingId: listing.listingId, // Keep original listingId
          listingItemId: listing.listingItemId, // Keep original listingItemId
          price: parseFloat(listing.price || 0),
          quantity: parseInt(listing.quantityRemaining || listing.quantity || 1),
          seller: listing.player || 'Unknown',
          totalPrice: parseFloat(listing.price || 0) * parseInt(listing.quantityRemaining || listing.quantity || 1),
          marketId: listing.listingId,
          detailId: listing.detailId,
          itemId: listing.itemId,
          currency: listing.currency,
          otherCurrencies: listing.otherCurrencies || [],
          createdDate: listing.createdDate,
          rawData: listing // Keep original data for debugging
        }));

        // Sort by price ascending
        const sortedListings = realListings.sort((a, b) => a.price - b.price);
        setListings(sortedListings);
        selectCheapestListings(sortedListings, quantity);
        return sortedListings;
      }

      // No listings found for this item
      setListings([]);
      setError(`No active listings found for ${item.detailName}.`);
      return [];
    } catch (err) {
      setError('Failed to load listings. Please try again.');
      setListings([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [item, quantity]);

  // Load listings when dialog opens
  useEffect(() => {
    if (open && item) {
      loadListings();
    }
  }, [open, item, loadListings]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setListings([]);
      setError(null);
      setQuantity(1);
      setSelectedListings([]);
    }
  }, [open]);

  // Select the cheapest listings to fulfill the desired quantity
  const selectCheapestListings = (availableListings, desiredQuantity) => {
    const selected = [];
    let remaining = desiredQuantity;
    
    for (const listing of availableListings) {
      if (remaining <= 0) break;
      
      const takeQuantity = Math.min(remaining, listing.quantity);
      selected.push({
        ...listing,
        selectedQuantity: takeQuantity,
        selectedPrice: listing.price * takeQuantity
      });
      
      remaining -= takeQuantity;
    }
    
    setSelectedListings(selected);
  };

  // Handle quantity change
  const handleQuantityChange = (newQuantity) => {
    const qty = Math.max(1, parseInt(newQuantity) || 1);
    setQuantity(qty);
    selectCheapestListings(listings, qty);
  };

  // Calculate total cost
  const getTotalCost = () => {
    return selectedListings.reduce((total, listing) => total + listing.selectedPrice, 0);
  };

  // Toggle a specific listing row in/out of the selection - lets the user pick a
  // particular seller instead of always taking the auto-selected cheapest ones.
  const handleToggleListing = (listing) => {
    setSelectedListings(prev => {
      const isSelected = prev.some(sl => sl.id === listing.id);
      if (isSelected) {
        return prev.filter(sl => sl.id !== listing.id);
      }
      const alreadySelectedQty = prev.reduce((sum, sl) => sum + sl.selectedQuantity, 0);
      const remaining = Math.max(1, quantity - alreadySelectedQty);
      const takeQuantity = Math.min(remaining, listing.quantity);
      return [
        ...prev,
        { ...listing, selectedQuantity: takeQuantity, selectedPrice: listing.price * takeQuantity }
      ];
    });
  };

  // Adjust the quantity taken from one specific selected listing
  const handleSelectedQuantityChange = (listingId, newQuantity) => {
    setSelectedListings(prev => prev.map(sl => {
      if (sl.id !== listingId) return sl;
      const clamped = Math.max(1, Math.min(parseInt(newQuantity) || 1, sl.quantity));
      return { ...sl, selectedQuantity: clamped, selectedPrice: sl.price * clamped };
    }));
  };

  // Add the currently selected listings to the shared item cart (consistent with how
  // cards work - purchase happens later from the shopping cart, not directly here).
  const handleAddToCart = async () => {
    if (selectedListings.length === 0) return;

    setPurchasing(true);
    try {
      const totalQuantity = selectedListings.reduce((sum, l) => sum + l.selectedQuantity, 0);
      await addToItemCart(item, totalQuantity, selectedListings);
      setSnackbarMsg(`${totalQuantity} x ${item.detailName} added to cart!`);
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMsg(`Failed to add to cart: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setPurchasing(false);
    }
  };

  // Get asset type color
  const getAssetTypeColor = () => {
    switch (item?.assetName) {
      case 'PACKS': return '#4CAF50';
      case 'LAND': return '#8BC34A';
      case 'TOTEMS': return '#FF9800';
      case 'TITLES': return '#9C27B0';
      case 'DEEDS': return '#795548';
      case 'RESOURCES': return '#00BCD4';
      case 'TOTEM_ITEMS': return '#FF5722';
      case 'TOTEM_FRAGMENTS': return '#FFC107';
      case 'AVATARS': return '#E91E63';
      case 'CONSUMABLES': return '#3F51B5';
      default: return colors.grey[400];
    }
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(3)}`;
  };

  // Format price in different currencies
  const formatPriceMultiCurrency = (usdPrice) => {
    const decPrice = convertUsdToDec(usdPrice);
    const creditsPrice = convertUsdToCredits(usdPrice);
    
    return {
      usd: formatPriceToUsd(usdPrice),
      dec: formatPriceToDec(decPrice),
      credits: formatPriceToCredits(creditsPrice)
    };
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          color: colors.grey[100]
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.primary[500],
          color: colors.grey[100],
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <img
            src={item.detailImage}
            alt={item.detailName}
            style={{ width: 40, height: 40, objectFit: 'contain' }}
            onError={(e) => e.target.style.display = 'none'}
          />
          <Box>
            <Typography variant="h6">{item.detailName}</Typography>
            <Chip
              label={item.assetDescription}
              size="small"
              sx={{
                backgroundColor: getAssetTypeColor(),
                color: 'white',
                fontSize: '0.7rem'
              }}
            />
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: colors.grey[100] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Item Description */}
        {item.detailDescription && (
          <Alert
            icon={<InfoIcon />}
            severity="info"
            sx={{
              mb: 3,
              backgroundColor: colors.blueAccent[800],
              color: colors.grey[100],
              '& .MuiAlert-icon': { color: colors.blueAccent[400] }
            }}
          >
            {item.detailDescription}
          </Alert>
        )}

        {/* Quantity Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: colors.grey[100] }}>
            Purchase Quantity
          </Typography>
          <TextField
            type="number"
            value={quantity}
            onChange={(e) => handleQuantityChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ShoppingCartIcon sx={{ color: colors.grey[400] }} />
                </InputAdornment>
              ),
              inputProps: { min: 1, max: item.numListed }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: colors.grey[100],
                '& fieldset': { borderColor: colors.grey[400] },
                '&:hover fieldset': { borderColor: colors.grey[300] },
                '&.Mui-focused fieldset': { borderColor: getAssetTypeColor() }
              },
              '& .MuiInputLabel-root': { color: colors.grey[400] }
            }}
          />
        </Box>

        {/* Selected Listings Summary */}
        {selectedListings.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: colors.grey[100] }}>
              Selected Listings
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: colors.grey[400] }}>
              Cheapest listings are pre-selected - click any row below to pick a different seller instead.
            </Typography>
            <Box
              sx={{
                backgroundColor: colors.primary[500],
                borderRadius: 1,
                p: 2,
                mb: 2
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body1" sx={{ color: colors.grey[100] }}>
                  Total Quantity: {selectedListings.reduce((sum, listing) => sum + listing.selectedQuantity, 0)}
                </Typography>
              </Box>
              
              {/* Multi-currency price display */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ color: colors.grey[300], mb: 1 }}>
                  Total Cost:
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" sx={{ color: colors.blueAccent[400] }}>
                      {formatPriceMultiCurrency(getTotalCost()).usd}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                      USD
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" sx={{ color: colors.greenAccent[400], fontWeight: 'bold' }}>
                      {formatPriceMultiCurrency(getTotalCost()).dec}
                    </Typography>
                    <Avatar 
                      src="/assets/img_dec_256.png" 
                      alt="DEC" 
                      sx={{ width: 16, height: 16 }} 
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                      {formatPriceMultiCurrency(getTotalCost()).credits}
                    </Typography>
                    <Avatar 
                      sx={{ 
                        width: 16, 
                        height: 16,
                        bgcolor: '#FFD700',
                        color: 'black',
                        fontSize: '8px',
                        fontWeight: 'bold'
                      }}
                    >$</Avatar>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Listings Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress sx={{ color: getAssetTypeColor() }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: colors.primary[500],
              maxHeight: 400
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ backgroundColor: colors.primary[600] }} />
                  <TableCell sx={{ backgroundColor: colors.primary[600], color: colors.grey[100] }}>
                    Price
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[600], color: colors.grey[100] }}>
                    Quantity
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[600], color: colors.grey[100] }}>
                    Seller
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[600], color: colors.grey[100] }}>
                    Total
                  </TableCell>
                  <TableCell sx={{ backgroundColor: colors.primary[600], color: colors.grey[100] }}>
                    Selected
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listings.map((listing) => {
                  const selectedListing = selectedListings.find(sl => sl.id === listing.id);
                  const isSelected = !!selectedListing;

                  return (
                    <TableRow
                      key={listing.id}
                      hover
                      onClick={() => handleToggleListing(listing)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: isSelected ? `${getAssetTypeColor()}20` : 'transparent',
                        '&:hover': { backgroundColor: `${getAssetTypeColor()}10` }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleToggleListing(listing)}
                          sx={{ color: getAssetTypeColor(), '&.Mui-checked': { color: getAssetTypeColor() } }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: colors.grey[100] }}>
                        {formatPrice(listing.price)}
                      </TableCell>
                      <TableCell sx={{ color: colors.grey[100] }}>
                        {listing.quantity}
                      </TableCell>
                      <TableCell sx={{ color: colors.grey[100] }}>
                        {listing.seller}
                      </TableCell>
                      <TableCell sx={{ color: colors.grey[100] }}>
                        {formatPrice(listing.totalPrice)}
                      </TableCell>
                      <TableCell sx={{ color: colors.grey[100] }}>
                        {isSelected ? (
                          <TextField
                            type="number"
                            size="small"
                            value={selectedListing.selectedQuantity}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleSelectedQuantityChange(listing.id, e.target.value)}
                            inputProps={{ min: 1, max: listing.quantity, style: { width: '60px', color: colors.grey[100] } }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: colors.primary[500], p: 2 }}>
        <Button onClick={onClose} sx={{ color: colors.grey[300] }}>
          Cancel
        </Button>
        
        {selectedListings.length > 0 && (
          <Tooltip title={`Add to cart for ${formatPriceMultiCurrency(getTotalCost()).dec} (${formatPriceMultiCurrency(getTotalCost()).usd})`}>
            <Button
              variant="contained"
              disabled={purchasing}
              onClick={handleAddToCart}
              startIcon={purchasing ? <CircularProgress size={16} color="inherit" /> : <ShoppingCartIcon />}
              sx={{
                backgroundColor: colors.greenAccent[500],
                color: 'black',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: colors.greenAccent[700] },
                '&:disabled': { backgroundColor: colors.grey[600] }
              }}
            >
              Add to Cart
            </Button>
          </Tooltip>
        )}
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default ItemDetailsDialog;
